import React, { useState, useEffect } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import Modal from "./Modal";

/**
 * Reusable modal for admin hard-deletes.
 *
 * Lifecycle:
 *   1. On open, hits `/api/admin/delete/dependencies/{module}/{recordId}` to
 *      preview integrity-block / dependency count / high-impact status.
 *   2. Renders a warning panel with dependency counts.
 *   3. Requires:
 *        - reason (min 5 chars)
 *        - password (if high-impact)
 *        - typed ack phrase (if dependencies > 0)
 *   4. On confirm, DELETE to `endpoint` with body { reason, password, dependency_ack }.
 *
 * Props:
 *   open          bool
 *   onClose       fn
 *   onDeleted     fn(result)
 *   module        string  (matches the dependencies endpoint module key)
 *   recordId      string
 *   endpoint      string  — full path under /api, e.g. `/customers/{id}` (no leading /api)
 *   label         string  — short human label, e.g. customer name, order #
 *   testIdPrefix  string
 */
export default function AdminDeleteModal({
    open,
    onClose,
    onDeleted,
    module,
    recordId,
    endpoint,
    label,
    testIdPrefix = "admin-delete",
}) {
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState("");
    const [loading, setLoading] = useState(false);

    const [reason, setReason] = useState("");
    const [password, setPassword] = useState("");
    const [ack, setAck] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !module || !recordId) return;
        setPreview(null);
        setPreviewErr("");
        setReason("");
        setPassword("");
        setAck("");
        setError("");
        setLoading(true);
        api.post(`/admin/delete/dependencies/${module}/${recordId}`)
            .then((r) => setPreview(r.data))
            .catch((e) => setPreviewErr(formatApiError(e?.response?.data?.detail) || e.message))
            .finally(() => setLoading(false));
    }, [open, module, recordId]);

    if (!open) return null;

    const integrityBlocked = !!preview?.integrity_block;
    const dependencies = preview?.dependencies || {};
    const totalDeps = Object.values(dependencies).reduce((sum, v) => sum + Number(v || 0), 0);
    const ackRequired = preview?.ack_required;
    const highImpact = !!preview?.high_impact;

    const reasonOk = reason.trim().length >= 5;
    const passwordOk = !highImpact || password.length > 0;
    const ackOk = !ackRequired || ack.trim() === ackRequired;
    const canSubmit = !!preview && !integrityBlocked && reasonOk && passwordOk && ackOk && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setError("");
        try {
            const { data } = await api.delete(endpoint, {
                data: {
                    reason: reason.trim(),
                    password: highImpact ? password : undefined,
                    dependency_ack: ackRequired || undefined,
                },
            });
            onDeleted?.(data);
            onClose?.();
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={
                <span className="inline-flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-red-700" />
                    Delete {label || "record"}
                </span>
            }
            onClose={busy ? () => {} : onClose}
            size="md"
            testId={`${testIdPrefix}-modal`}
        >
            <div className="space-y-4">
                {loading && (
                    <div className="text-sm text-gray-500 italic">
                        Loading dependencies…
                    </div>
                )}

                {previewErr && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {previewErr}
                    </div>
                )}

                {preview && (
                    <>
                        <div className="flex gap-3 items-start border border-amber-200 bg-amber-50 text-amber-900 rounded-sm px-3 py-2">
                            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
                            <div className="text-sm leading-relaxed">
                                You are about to <strong>permanently delete</strong>{" "}
                                <span className="odr-mono">{preview.label}</span>. The full
                                record snapshot is captured in the audit log <em>before</em>{" "}
                                deletion. Global numbering counters will <strong>not</strong>{" "}
                                be reused.
                            </div>
                        </div>

                        {integrityBlocked && (
                            <div className="text-sm border border-red-300 bg-red-50 text-red-800 rounded-sm px-3 py-2">
                                <strong>Integrity block:</strong> {preview.integrity_block}
                            </div>
                        )}

                        {totalDeps > 0 && !integrityBlocked && (
                            <div className="border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 text-sm">
                                <div className="font-semibold mb-1">
                                    This record has linked operational data:
                                </div>
                                <ul className="list-disc ml-5 space-y-0.5">
                                    {Object.entries(dependencies).map(([k, v]) => (
                                        <li key={k} data-testid={`${testIdPrefix}-dep-${k}`}>
                                            <span className="odr-mono">{v}</span> {k.replace(/_/g, " ")}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-2 text-xs text-amber-900">
                                    Deletion will cascade where allowed. Shipped/finalized records
                                    are blocked separately.
                                </div>
                            </div>
                        )}

                        {!integrityBlocked && (
                            <>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                        Reason <span className="text-red-700">*</span>
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="Explain why (min 5 chars) — recorded in audit"
                                        className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm"
                                        data-testid={`${testIdPrefix}-reason`}
                                        autoFocus
                                    />
                                </div>

                                {highImpact && (
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                            Confirm your admin password{" "}
                                            <span className="text-red-700">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Your password"
                                            className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm odr-mono"
                                            data-testid={`${testIdPrefix}-password`}
                                        />
                                    </div>
                                )}

                                {ackRequired && (
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                            Type{" "}
                                            <span className="odr-mono text-red-700">
                                                {ackRequired}
                                            </span>{" "}
                                            to proceed
                                        </label>
                                        <input
                                            type="text"
                                            value={ack}
                                            onChange={(e) => setAck(e.target.value)}
                                            className="w-full rounded-sm border border-red-300 px-3 py-2 text-sm odr-mono"
                                            data-testid={`${testIdPrefix}-ack`}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {error && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        data-testid={`${testIdPrefix}-cancel`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        className="odr-btn rounded-sm bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-4 disabled:opacity-50"
                        data-testid={`${testIdPrefix}-confirm`}
                    >
                        {busy ? "Deleting…" : "Permanently delete"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
